import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import GalleryToolbar from './components/GalleryToolbar';
import GalleryGrid from './components/GalleryGrid';
import { drawingService } from '../../services/drawingService';
import { siteService } from '../../services/siteService';
import { useAuth } from '../../contexts/AuthContext';

const SORT_FNS = {
  upload_date_desc: (a, b) =>
    new Date(b?.upload_date || b?.created_at || 0) -
    new Date(a?.upload_date || a?.created_at || 0),
  upload_date_asc: (a, b) =>
    new Date(a?.upload_date || a?.created_at || 0) -
    new Date(b?.upload_date || b?.created_at || 0),
  revision_desc: (a, b) =>
    (b?.version_number || 0) - (a?.version_number || 0),
  revision_asc: (a, b) =>
    (a?.version_number || 0) - (b?.version_number || 0),
  name_asc: (a, b) =>
    (a?.drawing_name || '')?.localeCompare(b?.drawing_name || ''),
  name_desc: (a, b) =>
    (b?.drawing_name || '')?.localeCompare(a?.drawing_name || ''),
};

const DrawingsGallery = () => {
  const { userProfile } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const paramSiteId = searchParams?.get('siteId');

  const [allDrawings, setAllDrawings] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [selectedSite, setSelectedSite] = useState(paramSiteId || 'all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('upload_date_desc');

  const breadcrumbItems = [
    { label: 'Home', href: '/admin-dashboard' },
    { label: 'Drawings & Plans', href: '/drawings-plans' },
    { label: 'Gallery' },
  ];

  // ── Data loading ──────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const sitesResult = await siteService?.getAllSites();
      if (sitesResult?.error) throw sitesResult.error;
      const sitesList = sitesResult?.data || [];
      setSites(sitesList);

      // Determine which sites to load
      const targetSiteId = selectedSite !== 'all' ? selectedSite : null;

      let drawings = [];
      if (targetSiteId) {
        // Single site — use getLatestDrawings for performance
        let result = await drawingService?.getLatestDrawings(targetSiteId);
        if (result?.error) throw result.error;
        const site = sitesList?.find(s => s?.id === targetSiteId);
        drawings = (result?.data || [])?.map(d => ({
          ...d,
          site_name: site?.site_name || d?.site_name,
        }));
      } else {
        // All sites — load latest drawings per site in parallel
        const results = await Promise.all(
          sitesList?.map(async site => {
            const r = await drawingService?.getLatestDrawings(site?.id);
            return (r?.data || [])?.map(d => ({
              ...d,
              site_name: site?.site_name,
            }));
          })
        );
        drawings = results?.flat();
      }

      setAllDrawings(drawings);
    } catch (err) {
      console.error('[DrawingsGallery] load drawings failed:', { code: err?.code, statusCode: err?.statusCode, message: err?.message });
      if (err?.code === 'PGRST301' || err?.code === '42501') {
        setError(`Permission denied: ${err?.message ?? 'unknown'}`);
      } else {
        setError(`Failed to load drawings: ${err?.message ?? 'unknown error'}`);
      }
    } finally {
      setLoading(false);
    }
  }, [selectedSite]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Sync site param from URL
  useEffect(() => {
    if (paramSiteId && paramSiteId !== selectedSite) {
      setSelectedSite(paramSiteId);
    }
  }, [paramSiteId]);

  // ── Filtering + sorting ───────────────────────────────────────────────────
  const filteredDrawings = useMemo(() => {
    let result = allDrawings;

    if (searchQuery?.trim()) {
      const q = searchQuery?.toLowerCase()?.trim();
      result = result?.filter(
        d =>
          d?.drawing_name?.toLowerCase()?.includes(q) ||
          d?.drawing_number?.toLowerCase()?.includes(q)
      );
    }

    const sortFn = SORT_FNS?.[sortBy] || SORT_FNS?.upload_date_desc;
    return [...result]?.sort(sortFn);
  }, [allDrawings, searchQuery, sortBy]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleSiteChange = (siteId) => {
    setSelectedSite(siteId);
    if (siteId !== 'all') {
      setSearchParams({ siteId });
    } else {
      setSearchParams({});
    }
  };

  return (
    <>
      <Helmet>
        <title>Drawings Gallery – SiteProof</title>
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />

        <main className="pt-20 pb-10 px-4 lg:px-6 max-w-screen-2xl mx-auto">
          {/* Page header */}
          <div className="mb-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-1">
                Drawings Gallery
              </h1>
              <p className="text-sm text-muted-foreground">
                Visual overview of all site drawings — click any card to open the detail view
              </p>
            </div>

            {/* Stats chip */}
            {!loading && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-accent/10 rounded-lg">
                <Icon name="LayoutGrid" size={15} color="var(--color-accent)" />
                <span className="text-sm font-semibold text-accent">
                  {filteredDrawings?.length} drawing{filteredDrawings?.length !== 1 ? 's' : ''}
                </span>
              </div>
            )}
          </div>

          {/* Toolbar */}
          <GalleryToolbar
            sites={sites}
            selectedSite={selectedSite}
            onSiteChange={handleSiteChange}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            sortBy={sortBy}
            onSortChange={setSortBy}
            totalCount={allDrawings?.length}
            filteredCount={filteredDrawings?.length}
          />

          {/* Grid */}
          <GalleryGrid
            drawings={filteredDrawings}
            loading={loading}
            error={error}
          />
        </main>
      </div>
    </>
  );
};

export default DrawingsGallery;
